import { useState } from 'react';
import {
  Datagrid,
  FunctionField,
  List,
  TextField,
  usePermissions,
  useRefresh,
  useTranslate,
} from 'react-admin';
import {
  Avatar,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  Typography,
} from '@mui/material';
import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { EmptyContent } from 'src/components/empty-content';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionData } from 'src/pages/admin/entities/permission.entity';
import { hasPermission } from 'src/pages/admin/utils/has-permission';
import { updateChainConfig } from '../../../services/xpxPerpsChainTokenConfigService';
import './init';

const PERMISSION_RESOURCE = 'xpx-perps:chain-config';

type TogglePending = {
  record: any;
  field: 'depositEnabled' | 'withdrawalEnabled';
  nextValue: boolean;
};

const XpxPerpsChainConfigsList = () => {
  const t = useTranslate();
  const refresh = useRefresh();
  const { permissions } = usePermissions<PermissionData>();
  const canWrite = hasPermission(permissions?.permissions, PERMISSION_RESOURCE, 'write');
  const [pending, setPending] = useState<TogglePending | null>(null);
  const [loading, setLoading] = useState(false);

  const handleToggleClick = (
    record: any,
    field: 'depositEnabled' | 'withdrawalEnabled',
    nextValue: boolean,
  ) => {
    setPending({ record, field, nextValue });
  };

  const handleConfirm = async () => {
    if (!pending) return;
    setLoading(true);
    try {
      const { record, field, nextValue } = pending;
      await updateChainConfig(
        record.chainId,
        field === 'depositEnabled' ? nextValue : record.depositEnabled,
        field === 'withdrawalEnabled' ? nextValue : record.withdrawalEnabled,
      );
      refresh();
    } finally {
      setLoading(false);
      setPending(null);
    }
  };

  const handleCancel = () => setPending(null);

  const action = pending?.nextValue
    ? t('module.xpxPerpsChainConfigs.confirmEnable')
    : t('module.xpxPerpsChainConfigs.confirmDisable');
  const field = pending ? t(`module.xpxPerpsChainConfigs.${pending.field}`) : '';

  return (
    <DashboardContent>
      <PermissionGuard resource={PERMISSION_RESOURCE} action="read" fallback={<ErrorPage403 />}>
        <CustomBreadcrumbs
          heading={t('module.xpxPerpsChainConfigs.title')}
          links={[
            { name: t('resource.perpsCore'), href: '#' },
            { name: t('module.xpxPerpsChainConfigs.title') },
            { name: t('resource.list') },
          ]}
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List actions={false} empty={false} perPage={100} exporter={false}>
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid rowClick={false} bulkActionButtons={false} empty={<EmptyContent />}>
                <FunctionField
                  label="module.xpxPerpsChainConfigs.logo"
                  sortable={false}
                  render={(record: any) => (
                    <Avatar src={record.logo} alt={record.name} sx={{ width: 28, height: 28 }} />
                  )}
                />
                <TextField
                  source="chainId"
                  label="module.xpxPerpsChainConfigs.chainId"
                  sortable={false}
                />
                <TextField
                  source="name"
                  label="module.xpxPerpsChainConfigs.name"
                  sortable={false}
                />
                <TextField
                  source="chainType"
                  label="module.xpxPerpsChainConfigs.chainType"
                  sortable={false}
                />
                <FunctionField
                  label="module.xpxPerpsChainConfigs.depositEnabled"
                  sortable={false}
                  render={(record: any) => (
                    <Switch
                      checked={record.depositEnabled}
                      size="small"
                      disabled={!canWrite}
                      onChange={(e) =>
                        handleToggleClick(record, 'depositEnabled', e.target.checked)
                      }
                    />
                  )}
                />
                <FunctionField
                  label="module.xpxPerpsChainConfigs.withdrawalEnabled"
                  sortable={false}
                  render={(record: any) => (
                    <Switch
                      checked={record.withdrawalEnabled}
                      size="small"
                      disabled={!canWrite}
                      onChange={(e) =>
                        handleToggleClick(record, 'withdrawalEnabled', e.target.checked)
                      }
                    />
                  )}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <Dialog open={Boolean(pending)} onClose={handleCancel} maxWidth="xs" fullWidth>
          <DialogTitle>{t('module.xpxPerpsChainConfigs.confirmTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              {t('module.xpxPerpsChainConfigs.confirmMessage', {
                action,
                field,
                name: pending?.record?.name ?? '',
              })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} disabled={loading}>
              {t('ra.action.cancel')}
            </Button>
            <Button onClick={handleConfirm} variant="contained" disabled={loading || !canWrite}>
              {t('ra.action.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default XpxPerpsChainConfigsList;
